export interface SeedPreviewItem {
  label: string
  color?: string
}

type TypeSeedLabels = Record<string, string[]>

const COMMON_SEED_PREVIEW: Record<string, SeedPreviewItem[]> = {
  'Conversation Statuses': [
    { label: 'New', color: '#F59E0B' },
    { label: 'Open', color: '#3B82F6' },
    { label: 'Pending', color: '#F97316' },
    { label: 'Resolved', color: '#10B981' },
    { label: 'Closed', color: '#6B7280' },
  ],
  'Conversation States': [
    { label: 'New', color: '#F59E0B' },
    { label: 'Open', color: '#3B82F6' },
    { label: 'In Progress', color: '#F97316' },
    { label: 'Resolved', color: '#10B981' },
    { label: 'Closed', color: '#6B7280' },
  ],
  'Conversation Priorities': [
    { label: 'Low', color: '#10B981' },
    { label: 'Medium', color: '#F59E0B' },
    { label: 'High', color: '#F97316' },
    { label: 'Urgent', color: '#EF4444' },
  ],
  'Conversation Urgencies': [
    { label: 'Low', color: '#10B981' },
    { label: 'Medium', color: '#3B82F6' },
    { label: 'High', color: '#EF4444' },
  ],
  'Conversation Impacts': [
    { label: 'Low', color: '#10B981' },
    { label: 'Medium', color: '#F59E0B' },
    { label: 'High', color: '#EF4444' },
  ],
  'Conversation Complexities': [
    { label: 'Simple', color: '#10B981' },
    { label: 'Moderate', color: '#F59E0B' },
    { label: 'Complex', color: '#EF4444' },
  ],
  'Approval States': [
    { label: 'Pending', color: '#F59E0B' },
    { label: 'Approved', color: '#10B981' },
    { label: 'Rejected', color: '#EF4444' },
    { label: 'Escalated', color: '#7C3AED' },
  ],
  'Email Templates': [
    { label: 'Default Reply Template' },
    { label: 'User Created Password' },
    { label: 'Password Reset' },
  ],
  'SMS Templates': [
    { label: 'Default Reply SMS' },
    { label: 'User Created Password SMS' },
    { label: 'Password Reset SMS' },
  ],
  'WhatsApp Templates': [
    { label: 'default_reply' },
    { label: 'user_created_password' },
    { label: 'password_reset' },
  ],
}

const COMPANY_TYPE_SEED_LABELS: Record<string, TypeSeedLabels> = {
  general: {
    'Conversation Types': ['Support', 'Sales', 'Billing'],
    'Conversation Categories': ['Support - General', 'Sales - General', 'Billing - General'],
    'Conversation Subcategories': ['General Inquiry', 'Lead', 'Invoice Issue'],
    'Approval Levels': ['Beginner', 'Intermediate', 'Advanced', 'Expert'],
  },
  bpo: {
    'Conversation Types': ['Inbound', 'Outbound', 'Back Office', 'Quality'],
    'Conversation Categories': ['Customer Service', 'Lead Generation', 'Complaints', 'Quality Assurance', 'Admin'],
    'Conversation Subcategories': ['Account Help', 'New Lead', 'Survey', 'Complaint', 'Documentation'],
    'Approval Levels': ['Operator', 'Senior Operator', 'Supervisor', 'Manager'],
  },
  healthcare: {
    'Conversation Types': ['Appointments', 'Billing', 'Clinical', 'Insurance'],
    'Conversation Categories': ['Scheduling', 'Payments', 'Lab', 'Pharmacy', 'Claims'],
    'Conversation Subcategories': ['New Appointment', 'Reschedule', 'Invoice', 'Lab Result', 'Prescription Refill', 'Prior Auth'],
    'Approval Levels': ['Clerk', 'Nurse', 'Specialist', 'Department Head'],
  },
  ecommerce: {
    'Conversation Types': ['Orders', 'Returns', 'Support', 'Shipping'],
    'Conversation Categories': ['Pre-sales', 'Order Issue', 'Refund', 'Delivery', 'Product Question'],
    'Conversation Subcategories': ['Size Exchange', 'Damaged Item', 'Track Order', 'Cancel Order', 'Product Info'],
    'Approval Levels': ['Support Agent', 'Senior Support', 'Team Lead', 'Manager'],
  },
  financial_services: {
    'Conversation Types': ['Accounts', 'Loans', 'Cards', 'Complaints'],
    'Conversation Categories': ['Retail', 'Corporate', 'Mortgage', 'Credit Card', 'Fraud'],
    'Conversation Subcategories': ['Balance Inquiry', 'Loan Application', 'Card Replacement', 'Dispute', 'KYC Update'],
    'Approval Levels': ['Associate', 'Officer', 'Manager', 'Director'],
  },
  logistics: {
    'Conversation Types': ['Shipment', 'Delivery', 'Returns', 'Billing'],
    'Conversation Categories': ['Pickup', 'Transit', 'Hub', 'Customs', 'Invoices'],
    'Conversation Subcategories': ['Schedule Pickup', 'In Transit', 'Delivery Attempt', 'Customs Hold', 'Freight Bill'],
    'Approval Levels': ['Associate', 'Supervisor', 'Manager', 'Director'],
  },
}

const previewCache = new Map<string, Record<string, SeedPreviewItem[]>>()

function normalizeCompanyType(companyType: string): string {
  return companyType.toLowerCase().trim().replace(/\s+/g, '_')
}

export function getSeedPreviewData(companyType: string = 'general'): Record<string, SeedPreviewItem[]> {
  const type = normalizeCompanyType(companyType)
  const cached = previewCache.get(type)
  if (cached !== undefined) {
    return cached
  }

  const selectedTypeLabels = COMPANY_TYPE_SEED_LABELS[type] ?? COMPANY_TYPE_SEED_LABELS.general!
  const typeSpecificPreview: Record<string, SeedPreviewItem[]> = {}
  for (const [group, labels] of Object.entries(selectedTypeLabels)) {
    typeSpecificPreview[group] = labels.map((label) => ({ label }))
  }

  const combined: Record<string, SeedPreviewItem[]> = {
    ...typeSpecificPreview,
    ...COMMON_SEED_PREVIEW,
  }

  previewCache.set(type, combined)
  return combined
}
